/** Painting the minimap into a raw RGBA buffer.
 *
 * Kept apart from the React component so it can be tested with no canvas, and
 * so the expensive part (one pass over up to 20,736 cells) has an obvious place
 * to be memoised on `version`.
 *
 * The buffer is one texel per map cell. Scaling to the panel is the canvas's
 * job and is done with smoothing off at a whole-number ratio (`fitMinimap`), so
 * the coastline stays as crisp as the terrain art it stands for.
 */

import { TERRAINS, type PlayerObservation, type TerrainKind } from "@game/core";
import { NEUTRAL_COLOUR, playerColour } from "../theme/tokens.js";

export type Rgb = readonly [number, number, number];

/**
 * Terrain at minimap scale is not the terrain palette shrunk: at one texel per
 * cell the eye reads *region*, so these are flattened, slightly desaturated
 * versions chosen to stay distinct from each other and from the owner colours.
 */
export const TERRAIN_MINIMAP_COLOUR: Readonly<Record<TerrainKind, Rgb>> = Object.freeze({
  water: [46, 84, 150],
  sand: [206, 184, 124],
  dirt: [126, 96, 64],
  grass: [92, 142, 62],
  snow: [214, 222, 228],
  swamp: [92, 114, 80],
  rough: [148, 130, 84],
  cavern: [102, 76, 70],
  lava: [92, 60, 52],
  rock: [110, 116, 120],
  // The two the expansion terrains. Sampled from the art the way the ten above were -
  // `TILES/assets/terrain/<id>/master.png` averages to (113, 109, 68) and
  // (118, 99, 89) - then lifted and pushed apart. Highlands is the one that
  // needed the pushing: at its sampled value it sat 24 units from swamp, which
  // at one texel per cell is the same colour.
  highlands: [132, 160, 92],
  wasteland: [128, 116, 100],
});

/**
 * Unexplored ground: one flat cool slate, clearly above the panel's own black
 * and in the same family as the shroud the WORLD renderer paints.
 *
 * It has to be ONE value - anything that varies with the terrain under it is a
 * tint whose shape is the shape of the map, and that leaks the map. What it
 * must not be is the black of the well it sits in. At [6,7,16] it was, and on
 * day one, when nine tenths of the minimap is this value, the panel read as a
 * failed render: a black square with a green smudge in the corner. Lifting it
 * to a slate that is unmistakably *a surface* costs nothing and leaks nothing,
 * because every unexplored cell still carries the identical byte.
 *
 * The value it was lifted TO, [30,34,45], was picked against the panel and not
 * against the map, and the two then disagreed: sampled on the shipped
 * 1920x1080 night frame the world's own unexplored shroud reads (2,2,12), so
 * the minimap's fog was 28 grey levels lighter and a degree cooler than the
 * thing it stands for - one screen showing two different colours for one
 * state. This is the same hue held at a value that still reads as a surface in
 * a gilt well: it stays 20 levels clear of the panel's black and closes half
 * the distance to the map's own.
 *
 * `Minimap.compose` draws a gilt hairline round the image as well, so the map's
 * extent is legible before any of it is explored.
 */
const FOG_BLACK: Rgb = [17, 19, 30];

/** Remembered ground is the terrain colour, dimmed. The classic greys it; dimming
 *  keeps the shape of the landmass readable, which is the point of the memory. */
const REMEMBERED_MUL = 0.45;

/** 0 unseen, 1 remembered, 2 visible - `ObservedMap.visibility`. */
const UNSEEN = 0;
const REMEMBERED = 1;

export interface MinimapMarker {
  readonly x: number;
  readonly y: number;
  readonly colour: string;
  /** Half-extent in cells: 0 paints one texel, 1 paints a 3x3 block. */
  readonly radius: number;
}

/** `#rrggbb` -> bytes. Anything else lands on neutral rather than throwing. */
export function parseHex(colour: string): Rgb {
  const s = colour.trim();
  if (s.length === 7 && s.charCodeAt(0) === 35) {
    const n = Number.parseInt(s.slice(1), 16);
    if (Number.isFinite(n)) return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const fallback = Number.parseInt(NEUTRAL_COLOUR.slice(1), 16);
  return [(fallback >> 16) & 255, (fallback >> 8) & 255, fallback & 255];
}

/** Bytes needed for a map of this size. */
export const minimapBufferSize = (width: number, height: number): number => width * height * 4;

/**
 * Terrain and fog for one level. `out` must be `minimapBufferSize` long.
 * Everything unseen is one flat value - the fog is not a tint over known
 * ground, it is the absence of ground, and drawing it any other way leaks the
 * map shape.
 */
export function paintMinimapTerrain(obs: PlayerObservation, level: number, out: Uint8ClampedArray): void {
  const { width, height } = obs.map;
  const plane = level * width * height;
  for (let i = 0; i < width * height; i++) {
    const cell = plane + i;
    const vis = obs.map.visibility[cell] ?? UNSEEN;
    const o = i * 4;
    if (vis === UNSEEN) {
      out[o] = FOG_BLACK[0]; out[o + 1] = FOG_BLACK[1]; out[o + 2] = FOG_BLACK[2]; out[o + 3] = 255;
      continue;
    }
    const kind = TERRAINS[obs.map.terrain[cell] ?? 0] ?? "rock";
    const rgb = TERRAIN_MINIMAP_COLOUR[kind];
    const mul = vis === REMEMBERED ? REMEMBERED_MUL : 1;
    // A road is the one overlay worth a texel: it is how a player reads a route
    // at a glance, and it survives the dimming because it is drawn after it.
    const road = (obs.map.road[cell] ?? 0) > 0;
    const lift = road ? 40 : 0;
    out[o] = rgb[0] * mul + lift;
    out[o + 1] = rgb[1] * mul + lift;
    out[o + 2] = rgb[2] * mul + lift;
    out[o + 3] = 255;
  }
}

/**
 * Owner marks, painted over the terrain pass.
 *
 * Each mark gets a one-texel dark halo before its colour goes down. Without it
 * a green player's town on grass and a tan player's mine on sand are invisible,
 * which defeats the only job the minimap has: telling you where everyone is
 * without looking at the map.
 */
export function paintMinimapMarkers(
  markers: readonly MinimapMarker[], width: number, height: number, out: Uint8ClampedArray,
): void {
  const put = (x: number, y: number, r: number, g: number, b: number): void => {
    if (x < 0 || y < 0 || x >= width || y >= height) return;
    const o = (y * width + x) * 4;
    out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = 255;
  };

  // Two passes, and the halo is a RING rather than a filled block.
  //
  // Filling the whole (2r+3)-square with near-black and then covering the inner
  // (2r+1) with colour left a one-texel black frame - which is correct - but a
  // second marker's halo landed on the FIRST marker's colour, because each
  // marker was finished before the next was begun. The hero standing beside his
  // town punched a black bite out of the town's own mark, and at twelve texels
  // to the pixel that mark read as a corrupt tile with a red hook in it rather
  // than as a town. Every halo first, every colour second, and no halo texel is
  // written inside its own marker.
  for (const marker of markers) {
    const rad = Math.max(0, Math.trunc(marker.radius));
    for (let dy = -rad - 1; dy <= rad + 1; dy++) {
      for (let dx = -rad - 1; dx <= rad + 1; dx++) {
        if (Math.abs(dx) <= rad && Math.abs(dy) <= rad) continue;
        put(marker.x + dx, marker.y + dy, MARKER_HALO[0], MARKER_HALO[1], MARKER_HALO[2]);
      }
    }
  }
  for (const marker of markers) {
    const [r, g, b] = parseHex(marker.colour);
    const rad = Math.max(0, Math.trunc(marker.radius));
    for (let dy = -rad; dy <= rad; dy++) {
      for (let dx = -rad; dx <= rad; dx++) put(marker.x + dx, marker.y + dy, r, g, b);
    }
  }
}

/**
 * The ring a marker wears, and it is a SEAT rather than a keyline.
 *
 * At [12,12,16] it was near-black, and near-black against snow at
 * [214,222,228] is a 200-level step for a one-texel ring - at five pixels to
 * the cell that reads as a hole punched round every town on the northern half
 * of the map, which is the same failure the unowned-mine markers were removed
 * for. What a marker needs is separation from the ground, not the maximum
 * separation available: a warm dark slate is 170 levels from snow, 60 from
 * grass and 130 from the fog, so it does the job on every terrain the palette
 * above carries and stops being the loudest thing on the image.
 */
const MARKER_HALO: Rgb = [40, 36, 44];

/**
 * The markers a player is entitled to see, in paint order: mines first, then
 * towns (a town is the landmark, so it wins the texel), then own heroes.
 *
 * Only what the observation already contains - the fog masking that decides
 * whether an owner is knowable happened in `observe()` (I10), and a null owner
 * here means "remembered but not currently lit", which is drawn neutral.
 */
export function minimapMarkers(obs: PlayerObservation, level: number): readonly MinimapMarker[] {
  const markers: MinimapMarker[] = [];
  const colourOf = (owner: string | null): string =>
    owner === null ? NEUTRAL_COLOUR : playerColour(colourFor(obs, owner));

  // Only what somebody OWNS. Every mine and dwelling on the map used to get a
  // texel, and an unowned one was painted the neutral grey inside a near-black
  // halo - so at five pixels to the cell the minimap wore a scatter of black
  // squares with a dark dot in each, which a reviewer read as holes punched
  // through the image. The classic's minimap answers "who holds what", and an unowned
  // mine is not an answer to that.
  for (const o of obs.objects) {
    if (o.z !== level) continue;
    if (o.owner === null) continue;
    if (o.kind !== "mine" && o.kind !== "dwelling") continue;
    markers.push({ x: o.x, y: o.y, colour: colourOf(o.owner as string | null), radius: 0 });
  }
  for (const t of obs.towns) {
    if (t.z !== level) continue;
    markers.push({ x: t.x, y: t.y, colour: colourOf(t.owner as string | null), radius: 1 });
  }
  // A hero stays one texel: the halo already makes it findable, and a 3x3 hero
  // standing next to a 3x3 town merges the two into one blob at map scale.
  for (const h of obs.heroes) {
    if (h.z !== level) continue;
    markers.push({ x: h.x, y: h.y, colour: playerColour(colourFor(obs, h.owner as string)), radius: 0 });
  }
  return markers;
}

/** A player id -> its `colour` string, from whichever side of the observation knows it. */
export function colourFor(obs: PlayerObservation, playerId: string): string {
  if (obs.self !== null && (obs.self.id as string) === playerId) return obs.self.colour;
  for (const opp of obs.opponents) {
    if ((opp.id as string) === playerId) return opp.colour;
  }
  return NEUTRAL_COLOUR;
}
